/* @flow */

import React from 'react'
import styled from 'styled-components'
import { Modal } from 'react-bootstrap'
import { Link } from 'react-router-dom'

import ActionButton from '../../infrastructure/components/ActionButton'
import CloudinaryResource from '../../infrastructure/components/CloudinaryResource'
import FormatCurrency from '../../infrastructure/components/FormatCurrency'
import DownloadDropShippingImagesModal from './DownloadDropShippingImagesModal'

import { ensurePublicDataAccessCode } from '../customers/api'
import { exportDropShippingProducts, exportDropShippingImages } from './api'
import { useProductsAvailableForDropShipping } from './hooks'

import { downloadPublicFile, msg } from '../shared'
import type { AvailableBrand, Id } from '../types'

const DropShippingModal = ({
  brand,
  onHide,
  onUpdateCustomer,
  show,
}: {
  brand: AvailableBrand,
  onHide: Function,
  onUpdateCustomer: Function,
  show: boolean,
}) => {
  const requestOptions = React.useMemo(() => [brand.id], [brand])
  const customer = React.useMemo(() => brand.retailer_connections[0], [brand])
  const [showDownloadImagesModal, setShowDownloadImagesModal] =
    React.useState(false)
  const [productsResponse] = useProductsAvailableForDropShipping(brand.id)

  const { currency, products } = productsResponse

  const onDownloadImages = React.useCallback(
    (productIds, transformations) => {
      return exportDropShippingImages(
        brand.id,
        productIds,
        transformations
      ).then(response => {
        if (!response.error) {
          setShowDownloadImagesModal(false)

          msg(
            'success',
            'We are preparing your images. An email with a link to download the images will be sent to your e-mail shortly.'
          )
        }
      })
    },
    [brand]
  )

  const onExportProductData = React.useCallback(
    (productIds, format) => {
      return exportDropShippingProducts(brand.id, productIds, format).then(
        response => {
          if (!response.error) {
            downloadPublicFile(response.payload)
          }
        }
      )
    },
    [brand]
  )

  const onEnsurePublicDataAccessCode = React.useCallback(() => {
    return ensurePublicDataAccessCode(customer.id).then(response => {
      if (!response.error) {
        onUpdateCustomer()
      }
    })
  }, [customer, onUpdateCustomer])

  return (
    <div>
      {showDownloadImagesModal !== false && (
        <DownloadDropShippingImagesModal
          onHide={() => setShowDownloadImagesModal(false)}
          onSubmit={transformations =>
            onDownloadImages(showDownloadImagesModal, transformations)
          }
          show={showDownloadImagesModal !== false}
        />
      )}

      <Modal show={show} onHide={onHide} dialogClassName="xl-modal">
        <Modal.Header closeButton>
          <Modal.Title>Dropshipping with {brand.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <iframe
            width="560"
            height="315"
            src="https://www.youtube.com/embed/tsVakGWl0ak"
            frameborder="0"
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
          ></iframe>

          <p>
            Below you can see all the products that {brand.name} offer's as part
            of their dropshipping program. When you purchase this product it
            will be sent directly to your customer's delivery address. The price
            you pay {brand.name} is the one entered as WHS. You can use the
            Export product data button to get inventory status and product data.
            Use the Download images button to download images for the products.
          </p>

          <div className="alert alert-info" style={{ marginBottom: 20 }}>
            <p>
              You can create a public accessible link to download {brand.name}{' '}
              product data anytime without having to login to Traede.
            </p>

            {!customer.public_link_code && (
              <>
                <div style={{ marginBottom: 10 }}>
                  <ActionButton
                    type="button"
                    className="btn btn-white"
                    onClick={onEnsurePublicDataAccessCode}
                  >
                    Create public link for product data (CSV)
                  </ActionButton>
                </div>

                <div>
                  <ActionButton
                    type="button"
                    className="btn btn-white"
                    onClick={onEnsurePublicDataAccessCode}
                  >
                    Create public link for product data (Excel)
                  </ActionButton>
                </div>
              </>
            )}

            {customer.public_link_code && (
              <div>
                <div>
                  <a
                    href={`${process.env.FULL_API_URL}/brands/${brand.id}/dropshipping/export-products-public/${customer.public_link_code}?format=csv`}
                  >
                    <strong>Product data in CSV Format (copy this link)</strong>
                  </a>
                </div>
                <div>
                  <a
                    href={`${process.env.FULL_API_URL}/brands/${brand.id}/dropshipping/export-products-public/${customer.public_link_code}?format=excel`}
                  >
                    <strong>
                      Product data in Excel Format (copy this link)
                    </strong>
                  </a>
                </div>
              </div>
            )}
          </div>

          <SectionTitle>Products available for drop shipping</SectionTitle>

          <table className="table table-condsened">
            <thead>
              <th className="listview-action" />
              <th>Product</th>
              <th width="300" className="text-right">
                Price
              </th>
              <th className="listview-action" />
              <th className="listview-action" />
              <th className="listview-action" />
            </thead>
            <tbody>
              {products.map(product => {
                return (
                  <tr key={product.id}>
                    <td>
                      <CloudinaryResource
                        id={product.image}
                        presets="table_medium"
                        fallback="table_medium"
                      />
                    </td>
                    <td>
                      <ProductName>{product.name}</ProductName>
                      {product.item_number}
                    </td>
                    <td className="text-right">
                      {product.price && customer && (
                        <div>
                          WHS:{' '}
                          <FormatCurrency currency={currency}>
                            {product.price.sales_price}
                          </FormatCurrency>
                        </div>
                      )}
                      {product.price && customer && (
                        <div>
                          RRP:{' '}
                          <FormatCurrency currency={currency}>
                            {product.price.rec_sales_price}
                          </FormatCurrency>
                        </div>
                      )}
                    </td>
                    <td>
                      <ActionButton
                        type="button"
                        className="btn btn-white btn-sm"
                        onClick={() => onExportProductData([product.id], 'csv')}
                      >
                        Export product data (CSV)
                      </ActionButton>
                    </td>
                    <td>
                      <ActionButton
                        type="button"
                        className="btn btn-white btn-sm"
                        onClick={() =>
                          onExportProductData([product.id], 'excel')
                        }
                      >
                        Export product data (Excel)
                      </ActionButton>
                    </td>
                    <td>
                      <ActionButton
                        type="button"
                        className="btn btn-white btn-sm"
                        onClick={() => setShowDownloadImagesModal([product.id])}
                      >
                        Download images
                      </ActionButton>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </Modal.Body>
        <Modal.Footer>
          <ActionButton
            type="button"
            className="btn btn-success"
            onClick={() =>
              onExportProductData(
                products.map(p => p.id),
                'csv'
              )
            }
          >
            Export all product data (CSV)
          </ActionButton>

          <ActionButton
            type="button"
            className="btn btn-success"
            onClick={() =>
              onExportProductData(
                products.map(p => p.id),
                'excel'
              )
            }
          >
            Export all product data (Excel)
          </ActionButton>

          <ActionButton
            type="button"
            className="btn btn-success"
            onClick={() => setShowDownloadImagesModal(products.map(p => p.id))}
          >
            Download all product images
          </ActionButton>

          <button type="button" className="btn btn-white" onClick={onHide}>
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default DropShippingModal

const ProductName = styled.div`
  font-weight: bold;
`

const SectionTitle = styled.div`
  color: black;
  font-size: 20px;
  margin-bottom: 10px;
`
